import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  BadgePercent, ArrowRight, MapPin, Store, Users, BookOpen,
  Check, X, Globe, Lightbulb
} from "lucide-react";

const PRO_SIGNUP = "/pro/register";
const DEMO_SRC = "/guestbook/prop_demo_hostland?embed=1&showcase=1";

// Social-proof numbers — fallback placeholders (admin-editable field wired in a later phase).
const SOCIAL_FALLBACK = { hosts: "500+", welcomebooks: "700+", guests: "20.000+" };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

// Contatore animato: parte da 0 e sale fino al valore finale quando entra in viewport.
const CountUp = ({ end = 0, duration = 1800 }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !started.current && end) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
          setValue(Math.floor(eased * end));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{value.toLocaleString("it-IT")}</span>;
};

// Reusable phone mockup that renders the live demo at 390px logical width and
// scales it to fit the frame (same technique as the homepage showcase).
const DemoPhone = () => {
  const screenRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [scale, setScale] = useState(0);
  const DEVICE_W = 390;
  const DEVICE_H = Math.round((DEVICE_W * 956) / 440);
  const wrapRef = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver(
      (e) => { if (e.some((x) => x.isIntersecting)) { setReady(true); io.disconnect(); } },
      { rootMargin: "200px 0px" }
    );
    if (wrapRef.current) io.observe(wrapRef.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const el = screenRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / DEVICE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready]);
  return (
    <div ref={wrapRef} className="relative mx-auto w-full max-w-[280px]" data-testid="pro-demo-phone">
      <div className="relative mx-auto" style={{ aspectRatio: "440/956" }}>
        <div className="absolute inset-0 rounded-[44px] shadow-2xl" style={{ background: "linear-gradient(135deg,#1f1f23,#0a0a0c 50%,#1f1f23)", padding: "10px", boxShadow: "0 30px 80px -20px rgba(124,58,237,0.4), 0 50px 120px -40px rgba(0,0,0,0.55)" }}>
          <div ref={screenRef} className="relative w-full h-full rounded-[34px] overflow-hidden bg-black">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 w-[28%] h-5 rounded-full bg-black" />
            {!(ready && scale > 0) && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-zinc-50 to-white">
                <div className="w-7 h-7 rounded-full border-2 border-zinc-200 border-t-zinc-500 animate-spin" />
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400">Demo live</span>
              </div>
            )}
            {ready && scale > 0 && (
              <div className="absolute top-0 left-0" style={{ width: DEVICE_W, height: DEVICE_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
                <iframe
                  src={DEMO_SRC}
                  title="Demo Welcome Book Hostland"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  className="border-0 bg-white block"
                  style={{ width: DEVICE_W, height: DEVICE_H }}
                  data-testid="pro-demo-iframe"
                />
              </div>
            )}
            <div className="absolute top-3 right-3 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[9px] font-black tracking-widest uppercase">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-80" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              Live
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ children, className = "", ...rest }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
    className={className}
    {...rest}
  >
    {children}
  </motion.section>
);

const BlackCTA = ({ children, onClick, testid }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.04, y: -2 }}
    whileTap={{ scale: 0.97 }}
    className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-zinc-900 text-white font-black text-base shadow-[0_16px_40px_-14px_rgba(0,0,0,0.7)] hover:bg-black transition-colors"
    data-testid={testid}
  >
    {children} <ArrowRight className="w-5 h-5" />
  </motion.button>
);

export default function ProLanding() {
  const navigate = useNavigate();
  const [social, setSocial] = useState(SOCIAL_FALLBACK);
  const [wbOpens, setWbOpens] = useState(0);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pro/social`)
      .then((r) => { if (r.data && r.data.hosts) setSocial(r.data); })
      .catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leaderboard/global-opens`)
      .then((r) => { if (r.data) setWbOpens(r.data.total_welcomebook_opens || 0); })
      .catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Hostland Pro",
    serviceType: "Local business promotion network for short-term rental guests",
    provider: { "@type": "Organization", name: "Hostland", url: "https://hostland.vitadahost.it", parentOrganization: { "@type": "Organization", name: "Vita da Host", url: "https://www.vitadahost.it" } },
    areaServed: "IT",
    description: "Hostland Pro è la rete che unisce le attività locali reali agli host della rete Hostland: sconti e promozioni a km zero, geolocalizzati, senza intermediazione delle grandi piattaforme pubblicitarie. Un canale diretto tra chi ospita e il territorio.",
    offers: { "@type": "Offer", price: "19.90", priceCurrency: "EUR", priceValidUntil: "2027-05-31", availability: "https://schema.org/InStock", url: "https://hostland.vitadahost.it/pro" },
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900 overflow-x-hidden" data-testid="pro-landing">
      <Helmet>
        <title>Hostland Pro · Attività locali e host, sconti a km zero senza intermediari</title>
        <meta name="description" content="Hostland Pro connette le attività locali reali con gli host della rete Hostland. Crea promozioni geolocalizzate che raggiungono gli ospiti nel welcome book, nel momento in cui decidono cosa fare. 19,90€/anno, nessuna intermediazione delle big tech." />
        <link rel="canonical" href="https://hostland.vitadahost.it/pro" />
        <meta property="og:title" content="Hostland Pro · La rete tra attività locali e host" />
        <meta property="og:description" content="Una rivoluzione per unire attività locali, promozione e turismo locale senza intermediazione delle big tech." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ── Header ── */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="mx-3 sm:mx-4 mt-3 sm:mt-4">
          <div className="max-w-6xl mx-auto rounded-2xl bg-white/80 backdrop-blur-xl border border-zinc-200/70 shadow-sm px-4 sm:px-6 py-3 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 shrink-0" aria-label="Home">
              <img src="/hostland-logo.png" alt="Hostland" className="w-7 h-7 rounded-[3px] object-cover" />
              <span className="text-lg font-black tracking-tight">Hostland <span className="text-zinc-400 font-bold">Pro</span></span>
            </a>
            <BlackCTA onClick={() => navigate(PRO_SIGNUP)} testid="pro-header-cta">Registrati</BlackCTA>
          </div>
        </div>
      </header>

      {/* ── 4.1 Hero ── */}
      <Section className="relative px-4 pt-32 md:pt-44 pb-16 md:pb-24 max-w-5xl mx-auto text-center">
        <div aria-hidden className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #7C3AED55, transparent 60%)" }} />
        <motion.span variants={fadeUp} className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 text-white text-xs font-black uppercase tracking-widest mb-6">
          <BadgePercent className="w-4 h-4" /> Hostland Pro
        </motion.span>
        <motion.div variants={fadeUp} custom={0} className="relative mb-5">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs sm:text-sm font-black shadow-lg shadow-emerald-500/30" data-testid="pro-offer-banner">
            🎉 Inserimento GRATIS — fino al 31/05/2027
          </span>
        </motion.div>
        <motion.h1 variants={fadeUp} custom={1} className="relative text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[1.03] mb-5">
          Hai un'attività? Sei un professionista?{" "}
          <span style={{ backgroundImage: "linear-gradient(105deg,#7C3AED,#EC4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Vuoi arrivare a centinaia di ospiti?</span>
        </motion.h1>
        <motion.p variants={fadeUp} custom={2} className="relative text-lg md:text-xl text-zinc-500 font-medium max-w-2xl mx-auto mb-8">
          Entra in Hostland Pro — il canale diretto tra le attività del territorio e gli ospiti in vacanza nella tua zona.
        </motion.p>
        <motion.div variants={fadeUp} custom={3} className="relative flex justify-center">
          <BlackCTA onClick={() => navigate(PRO_SIGNUP)} testid="pro-hero-cta">Entra in Hostland Pro</BlackCTA>
        </motion.div>
      </Section>

      {/* ── 4.2 Concept ── */}
      <Section className="px-4 py-16 md:py-24 bg-zinc-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-black tracking-tighter mb-6">Il punto d'incontro tra chi ospita e le attività locali <span className="text-violet-600">vere</span></motion.h2>
          <motion.p variants={fadeUp} custom={1} className="text-base md:text-lg text-zinc-600 leading-relaxed max-w-3xl mx-auto">
            Hostland Pro promuove il chilometro zero e la simbiosi tra host e territorio. Niente intermediazione delle grandi piattaforme pubblicitarie: le attività locali creano le proprie promozioni geolocalizzate e queste diventano disponibili per tutti gli host della stessa zona, che le attivano nel proprio welcome book. Un ecosistema in cui il turismo alimenta l'economia reale del quartiere, e ogni ospite scopre le eccellenze del posto direttamente da chi lo accoglie.
          </motion.p>
        </div>
      </Section>

      {/* ── 4.3 Cos'è il Welcome Book + demo ── */}
      <Section className="px-4 py-16 md:py-24 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-black tracking-tighter mb-5">Il canale che raggiunge l'ospite nel momento giusto</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-base md:text-lg text-zinc-600 leading-relaxed mb-4">
              Ogni struttura che usa Hostland invia al proprio ospite, appena inizia il soggiorno, un link a una webapp personalizzata — il <strong>welcome book</strong> — con tutto ciò che serve: come arrivare, come entrare in casa, cosa fare in zona, consigli e regole.
            </motion.p>
            <motion.p variants={fadeUp} custom={2} className="text-base md:text-lg text-zinc-800 font-semibold leading-relaxed mb-4">
              Il tuo sconto non finisce sepolto in una newsletter o in un volantino. Compare dentro lo strumento che l'ospite apre davvero, nel momento in cui sta decidendo cosa fare durante il suo soggiorno.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="text-sm text-zinc-500">
              Prova tu stesso: questo è esattamente ciò che i tuoi futuri clienti vedranno sul telefono, inviato loro dall'host appena inizia il soggiorno. Naviga fino alla sezione sconti, dove comparirebbe la tua promozione.
            </motion.p>
          </div>
          <motion.div variants={fadeUp} custom={1}>
            <DemoPhone />
          </motion.div>
        </div>
      </Section>

      {/* ── 4.4 Comparativa ── */}
      <Section className="px-4 py-16 md:py-24 bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto">
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-black tracking-tighter text-center mb-12">Perché funziona meglio della pubblicità tradizionale</motion.h2>
          <div className="grid md:grid-cols-2 gap-5">
            <motion.div variants={fadeUp} className="rounded-3xl p-7 bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 mb-4 text-zinc-400 font-black uppercase tracking-widest text-sm"><X className="w-5 h-5" /> Pubblicità tradizionale</div>
              <ul className="space-y-3 text-zinc-300">
                {["Costo fisso, spesso elevato", "Raggiunge chiunque, poco mirato", "Nessuna garanzia che chi la vede sia un turista in zona", "Non sai se arriva nel momento della decisione"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><X className="w-4 h-4 mt-0.5 text-rose-400 shrink-0" /><span>{x}</span></li>
                ))}
              </ul>
            </motion.div>
            <motion.div variants={fadeUp} custom={1} className="rounded-3xl p-7 border" style={{ background: "linear-gradient(135deg,#7C3AED22,#EC489922)", borderColor: "#7C3AED66" }}>
              <div className="flex items-center gap-2 mb-4 text-white font-black uppercase tracking-widest text-sm"><Check className="w-5 h-5 text-violet-300" /> Hostland Pro</div>
              <ul className="space-y-3 text-white">
                {["Raggiunge solo persone già fisicamente in zona, come ospiti", "Nel momento esatto in cui decidono cosa fare", "Solo 19,90€/anno di iscrizione", "Nessun costo fisso di advertising"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" /><span>{x}</span></li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ── 4.5 Numeri / prova sociale ── */}
      <Section className="px-4 py-16 md:py-24 max-w-5xl mx-auto">
        <motion.h2 variants={fadeUp} className="text-2xl md:text-4xl font-black tracking-tighter text-center mb-12">Una rete che cresce ogni giorno</motion.h2>

        {/* Contatore aperture Welcome Book — ciò che un inserzionista vede davvero */}
        <motion.div
          variants={fadeUp}
          className="relative overflow-hidden rounded-3xl p-8 md:p-10 mb-6 text-center text-white"
          style={{ background: "linear-gradient(135deg,#7C3AED,#EC4899)" }}
          data-testid="pro-welcomebook-opens-counter"
        >
          <div aria-hidden className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-white text-[11px] font-black uppercase tracking-widest mb-4">
              <BookOpen className="w-3.5 h-3.5" /> Aperture totali Welcome Book
            </div>
            <div className="text-5xl md:text-7xl font-black tracking-tighter tabular-nums leading-none" data-testid="pro-welcomebook-opens-value">
              <CountUp end={wbOpens} />
            </div>
            <p className="text-sm md:text-base text-white/85 font-semibold max-w-xl mx-auto mt-4">
              Welcome Book aperti dagli ospiti della rete Hostland — è qui che la tua promozione raggiunge chi è già in zona, nel momento in cui decide cosa fare.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { icon: Users, n: social.hosts, l: "Host nella rete", c: "#7C3AED" },
            { icon: BookOpen, n: social.welcomebooks, l: "Welcome Book attivi", c: "#EC4899" },
            { icon: MapPin, n: social.guests, l: "Ospiti raggiunti (stima)", c: "#F59E0B" },
          ].map((s, i) => (
            <motion.div key={s.l} variants={fadeUp} custom={i} className="rounded-3xl p-7 bg-zinc-50 border border-zinc-100 text-center">
              <div className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${s.c}18`, color: s.c }}><s.icon className="w-6 h-6" /></div>
              <div className="text-4xl font-black tracking-tighter mb-1">{s.n}</div>
              <div className="text-sm text-zinc-500 font-semibold">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── 4.6 Come funziona ── */}
      <Section className="px-4 py-16 md:py-24 bg-zinc-50">
        <div className="max-w-5xl mx-auto">
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-black tracking-tighter text-center mb-12">Come funziona</motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Store, t: "Ti registri", d: "Attivi Hostland Pro a 19,90€/anno." },
              { icon: MapPin, t: "Scegli la città", d: "Cerchi la tua città di riferimento." },
              { icon: BadgePercent, t: "Crei la promo", d: "Logo, descrizione e sconto della tua attività." },
              { icon: Globe, t: "Entri nella rete", d: "Sei visibile a tutti gli host della tua zona." },
            ].map((s, i) => (
              <motion.div key={s.t} variants={fadeUp} custom={i} className="relative rounded-3xl p-6 bg-white border border-zinc-100 shadow-sm">
                <span className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-zinc-900 text-white font-black flex items-center justify-center text-sm">{i + 1}</span>
                <div className="w-11 h-11 rounded-2xl mb-3 flex items-center justify-center bg-violet-100 text-violet-600"><s.icon className="w-5 h-5" /></div>
                <h3 className="font-black text-lg mb-1">{s.t}</h3>
                <p className="text-sm text-zinc-500">{s.d}</p>
              </motion.div>
            ))}
          </div>

          {/* Nota importante: è l'host a scegliere le promo */}
          <motion.div variants={fadeUp} custom={4} className="mt-10 rounded-3xl border-2 border-violet-200 bg-violet-50 p-6 md:p-8 flex items-start gap-4" data-testid="pro-host-choice-note">
            <span className="shrink-0 w-12 h-12 rounded-2xl bg-violet-600 text-white flex items-center justify-center shadow-sm">
              <Lightbulb className="w-6 h-6" />
            </span>
            <div>
              <h3 className="font-black text-lg md:text-xl text-zinc-900 mb-1.5">È l'host a scegliere quali promo mostrare</h3>
              <p className="text-sm md:text-base text-zinc-600 leading-relaxed">
                Il sistema propone <strong>tutte</strong> le offerte della zona a ogni host: è poi l'host a decidere quali attivare nei propri Welcome Book, in base a ciò che ritiene più utile per i suoi ospiti.
                Per questo <strong>più la tua offerta è interessante e appetibile, più host saranno convinti ad attivarla</strong> — e più ospiti la vedranno. Crea uno sconto reale e conveniente per distinguerti nella rete.
              </p>
            </div>
          </motion.div>
        </div>
      </Section>
      <Section className="px-4 py-16 md:py-20 max-w-3xl mx-auto text-center">
        <motion.p variants={fadeUp} className="text-lg md:text-2xl text-zinc-700 font-semibold leading-relaxed">
          Entrare in Hostland Pro significa entrare in un canale diretto con chi è già fisicamente presente sul territorio, senza intermediazione di piattaforme pubblicitarie generiche, con un costo fisso annuale prevedibile invece di un budget pubblicitario variabile e disperso.
        </motion.p>
      </Section>

      {/* ── 4.8 Footer CTA ── */}
      <Section className="px-4 py-16 md:py-24 text-center" style={{ background: "linear-gradient(135deg,#0a0a0c,#1f1f23)" }}>
        <div className="max-w-3xl mx-auto">
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-black tracking-tighter text-white mb-6">Entra anche tu nella rete Hostland Pro</motion.h2>
          <motion.div variants={fadeUp} custom={1} className="flex justify-center">
            <motion.button
              onClick={() => navigate(PRO_SIGNUP)}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-zinc-900 font-black text-base shadow-2xl hover:bg-zinc-100 transition-colors"
              data-testid="pro-footer-cta"
            >
              Registrati ora <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
          <motion.p variants={fadeUp} custom={2} className="text-sm text-white/60 mt-4">19,90€/anno, nessun vincolo, disdici quando vuoi.</motion.p>
        </div>
      </Section>

      {/* ── Standard footer ── */}
      <footer className="bg-zinc-950 text-zinc-400 px-4 py-10 text-center text-sm">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-3">
          <a href="/" className="flex items-center gap-2 text-white font-black"><img src="/hostland-logo.png" alt="Hostland" className="w-6 h-6 rounded-[3px]" /> Hostland</a>
          <p className="max-w-2xl">Hostland Pro è la rivoluzione per unire attività locali, promozione e turismo locale senza intermediazione delle big tech. Un canale diretto tra host e territorio, a km zero.</p>
          <div className="flex items-center gap-4 text-xs">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <a href="/login" className="hover:text-white transition-colors">Accedi</a>
            <a href="https://www.vitadahost.it" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Vita da Host</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
